// Analytics utilities for tracking user behavior and traffic

interface AnalyticsEvent {
  event_name: string;
  parameters: Record<string, any>;
  timestamp: string;
  user_id?: string;
  session_id?: string;
}

interface PageViewEvent {
  page_title: string;
  page_location: string;
  page_path: string;
  timestamp: string;
  user_id?: string;
  session_id?: string;
}

// Google Analytics 4 configuration
let gtag: (...args: any[]) => void;
let isInitialized = false;

/**
 * Initialize Google Analytics 4
 */
export const initializeAnalytics = (trackingId: string): void => {
  if (typeof window === 'undefined' || isInitialized) return;

  // Load Google Analytics script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${trackingId}`;
  document.head.appendChild(script);

  // Initialize dataLayer and gtag
  window.dataLayer = window.dataLayer || [];
  gtag = function(...args: any[]) {
    window.dataLayer.push(args);
  };
  window.gtag = gtag;

  // Configure Google Analytics
  gtag('js', new Date());
  gtag('config', trackingId, {
    anonymize_ip: true,
    cookie_flags: 'secure;samesite=strict',
    send_page_view: false, // We'll send page views manually
  });

  isInitialized = true;
};

/**
 * Track page views
 */
export const trackPageView = (page: string, title?: string): void => {
  if (!isInitialized || !gtag) return;

  const pageData = {
    page_title: title || document.title,
    page_location: window.location.href,
    page_path: page,
  };

  gtag('event', 'page_view', pageData);
  
  // Also send to our internal analytics
  sendToInternalAnalytics('page_view', pageData);
};

/**
 * Track custom events
 */
export const trackEvent = (eventName: string, parameters?: Record<string, any>): void => {
  if (!isInitialized || !gtag) return;

  const eventData = {
    event_name: eventName,
    ...parameters,
    timestamp: new Date().toISOString(),
  };

  gtag('event', eventName, parameters);
  
  // Also send to our internal analytics
  sendToInternalAnalytics(eventName, eventData);
};

/**
 * Track user interactions
 */
export const trackUserInteraction = (
  action: string,
  category: string,
  label?: string,
  value?: number
): void => {
  trackEvent('user_interaction', {
    action,
    category,
    label,
    value,
    page: window.location.pathname,
  });
};

/**
 * Track form submissions
 */
export const trackFormSubmission = (formName: string, success: boolean = true): void => {
  trackEvent('form_submission', {
    form_name: formName,
    success,
    page: window.location.pathname,
  });
};

/**
 * Track button clicks
 */
export const trackButtonClick = (buttonName: string, location: string): void => {
  trackEvent('button_click', {
    button_name: buttonName,
    location,
    page: window.location.pathname,
  });
};

/**
 * Track file downloads
 */
export const trackDownload = (fileName: string, fileType: string): void => {
  trackEvent('file_download', {
    file_name: fileName,
    file_type: fileType,
    page: window.location.pathname,
  });
};

/**
 * Track search queries
 */
export const trackSearch = (searchTerm: string, resultsCount?: number): void => {
  trackEvent('search', {
    search_term: searchTerm,
    results_count: resultsCount,
    page: window.location.pathname,
  });
};

/**
 * Track user engagement
 */
export const trackEngagement = (engagementType: string, duration?: number): void => {
  trackEvent('engagement', {
    engagement_type: engagementType,
    duration,
    page: window.location.pathname,
  });
};

/**
 * Track errors
 */
export const trackError = (errorMessage: string, errorType: string, fatal: boolean = false): void => {
  trackEvent('error', {
    error_message: errorMessage,
    error_type: errorType,
    fatal,
    page: window.location.pathname,
  });
};

/**
 * Track conversions
 */
export const trackConversion = (conversionType: string, value?: number, currency?: string): void => {
  trackEvent('conversion', {
    conversion_type: conversionType,
    value,
    currency: currency || 'USD',
    page: window.location.pathname,
  });
};

/**
 * Track user journey
 */
export const trackUserJourney = (step: string, stepNumber: number, totalSteps: number): void => {
  trackEvent('user_journey', {
    step,
    step_number: stepNumber,
    total_steps: totalSteps,
    progress: (stepNumber / totalSteps) * 100,
    page: window.location.pathname,
  });
};

/**
 * Track feature usage
 */
export const trackFeatureUsage = (featureName: string, action: string): void => {
  trackEvent('feature_usage', {
    feature_name: featureName,
    action,
    page: window.location.pathname,
  });
};

/**
 * Track performance metrics
 */
export const trackPerformance = (metricName: string, value: number, unit: string = 'ms'): void => {
  trackEvent('performance', {
    metric_name: metricName,
    value,
    unit,
    page: window.location.pathname,
  });
};

/**
 * Send data to internal analytics API
 */
const getClientUserAgent = (): string => {
  const nav = navigator as Navigator & {
    userAgentData?: {
      mobile?: boolean;
      platform?: string;
      brands?: Array<{ brand: string; version: string }>;
      getHighEntropyValues?: (hints: string[]) => Promise<Record<string, string>>;
    };
  };

  if (nav.userAgentData) {
    const brandList =
      nav.userAgentData.brands?.map((b) => `${b.brand}/${b.version}`).join(' ') ?? '';
    const platform = nav.userAgentData.platform ?? '';
    return `${brandList} ${platform}`.trim();
  }

  return 'unknown-user-agent';
};

const sendToInternalAnalytics = async (eventType: string, data: any): Promise<void> => {
  try {
    // Send to your internal analytics endpoint
    await fetch('/api/analytics/track', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        event_type: eventType,
        data,
        user_agent: getClientUserAgent(),
        referrer: document.referrer,
        timestamp: new Date().toISOString(),
      }),
    });
  } catch (error) {
    console.error('Failed to send analytics data:', error);
  }
};

/**
 * Get user session information
 */
export const getSessionInfo = () => {
  return {
    session_id: getOrCreateSessionId(),
    user_id: getUserId(),
    timestamp: new Date().toISOString(),
    page: window.location.pathname,
    referrer: document.referrer,
    user_agent: getClientUserAgent(),
  };
};

import { safeGetItem, safeSetItem } from './storage';

/**
 * Get or create session ID
 */
const getOrCreateSessionId = (): string => {
  let sessionId = safeGetItem('analytics_session_id', 'sessionStorage');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    safeSetItem('analytics_session_id', sessionId, 'sessionStorage');
  }
  return sessionId;
};

/**
 * Get user ID from localStorage or create new one
 */
const getUserId = (): string => {
  let userId = safeGetItem('analytics_user_id', 'localStorage');
  if (!userId) {
    userId = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    safeSetItem('analytics_user_id', userId, 'localStorage');
  }
  return userId;
};

/**
 * Track scroll depth
 */
export const trackScrollDepth = (depth: number): void => {
  trackEvent('scroll_depth', {
    depth,
    page: window.location.pathname,
  });
};

/**
 * Track time on page
 */
export const trackTimeOnPage = (timeSpent: number): void => {
  trackEvent('time_on_page', {
    time_spent: timeSpent,
    page: window.location.pathname,
  });
};

// Extend Window interface
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}
